import * as Sentry from '@sentry/nextjs';
import React, { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { emailRegex } from '@winging/util/helper/regex';
import { useAuth } from '@/shared/providers/AuthProvider';
import { LabeledInputStatus } from '@/shared/ui/LabeledInput';
import { useToast } from '@/shared/providers/ToastProvider';
import SignIn from '@/features/auth/ui/SignIn';
import EmailMfaVerification from '@/features/auth/ui/EmailMfaVerification';

const LandingSignIn = () => {
  const t = useTranslations('Auth');
  const { signIn, verifySignInMfa, resendSignInMfa } = useAuth();
  const { showToast } = useToast();
  const mfaSubmittingRef = useRef(false);
  const mfaResendingRef = useRef(false);

  const [passwordInputType, setPasswordInputType] = useState('password');
  const [isPersistent, setIsPersistent] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    maskedEmail: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStatus, setMfaStatus] = useState<LabeledInputStatus>('');
  const [mfaMsg, setMfaMsg] = useState('');
  const [isMfaSubmitting, setIsMfaSubmitting] = useState(false);
  const [isMfaResending, setIsMfaResending] = useState(false);

  const [emailMsg, setEmailMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const [emailStatus, setEmailStatus] = useState<LabeledInputStatus>('');
  const [passwordStatus, setPasswordStatus] = useState<LabeledInputStatus>('');

  const validate = () => {
    let valid = true;
    if (!email) {
      valid = false;
      setEmailMsg(t('이메일 주소가 입력되지 않았습니다'));
      setEmailStatus('error');
    } else if (!emailRegex.test(email)) {
      valid = false;
      setEmailMsg(t('이메일 형식으로 올바르게 입력해주세요'));
      setEmailStatus('error');
    } else {
      setEmailMsg('');
      setEmailStatus('');
    }
    if (!password) {
      valid = false;
      setPasswordMsg(t('비밀번호가 입력되지 않았습니다'));
      setPasswordStatus('error');
    } else {
      setPasswordMsg('');
      setPasswordStatus('');
    }
    return valid;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    try {
      const result = await signIn({
        email,
        password,
        isPersistent,
      });
      if (result.mfaRequired) {
        setMfaChallenge(result);
      }
    } catch (e) {
      Sentry.captureException(e);
      if (e?.response?.data?.message?.startsWith('invalid_password')) {
        setPasswordStatus('error');
        setPasswordMsg(t('비밀번호가 올바르지 않습니다'));
        const attemptCount =
          e.response.data.message.split('_')[
            e.response.data.message.split('_').length - 1
          ];
        showToast(
          `${t('비밀번호를 10회 잘못 입력하면 로그인이 1분간 제한됩니다')} (${attemptCount}/10)`
        );
      } else if (e?.response?.data?.message === 'not_found_user') {
        setEmailStatus('error');
        setEmailMsg(t('가입되지 않은 계정입니다'));
      } else if (e?.response?.data?.message === 'mfa_code_recently_sent') {
        showToast(t('인증번호가 이미 전송되었습니다'));
      } else {
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
    }
  };

  const submitMfa = async () => {
    if (mfaSubmittingRef.current || mfaResendingRef.current) return;
    if (!mfaChallenge || !/^\d{6}$/.test(mfaCode)) {
      setMfaStatus('error');
      setMfaMsg(t('6자리 인증번호를 입력해 주세요'));
      return;
    }
    mfaSubmittingRef.current = true;
    setIsMfaSubmitting(true);
    try {
      await verifySignInMfa({
        challengeId: mfaChallenge.challengeId,
        code: mfaCode,
      });
    } catch (e) {
      const message = e?.response?.data?.message;
      setMfaStatus('error');
      if (message === 'invalid_mfa_code') {
        setMfaMsg(t('인증번호가 올바르지 않습니다'));
      } else if (message === 'mfa_challenge_expired') {
        setMfaMsg(t('인증번호가 만료되었습니다'));
      } else if (message === 'mfa_too_many_attempts') {
        setMfaMsg(t('인증 시도 횟수를 초과했습니다'));
      } else if (message === 'mfa_verification_in_progress') {
        setMfaMsg(t('인증을 처리하고 있습니다'));
      } else {
        Sentry.captureException(e);
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
    } finally {
      mfaSubmittingRef.current = false;
      setIsMfaSubmitting(false);
    }
  };

  const resendMfa = async (): Promise<boolean> => {
    if (!mfaChallenge || mfaSubmittingRef.current || mfaResendingRef.current)
      return false;
    mfaResendingRef.current = true;
    setIsMfaResending(true);
    try {
      await resendSignInMfa({ challengeId: mfaChallenge.challengeId });
      setMfaCode('');
      setMfaStatus('');
      setMfaMsg('');
      showToast(t('인증번호가 재전송되었습니다'));
      return true;
    } catch (e) {
      const message = e?.response?.data?.message;
      if (message === 'mfa_code_recently_sent') {
        showToast(t('인증번호가 이미 전송되었습니다'));
      } else if (message === 'mfa_challenge_expired') {
        setMfaStatus('error');
        setMfaMsg(t('인증번호가 만료되었습니다'));
      } else {
        Sentry.captureException(e);
        showToast(t('알 수 없는 에러가 발생했습니다'));
      }
      return false;
    } finally {
      mfaResendingRef.current = false;
      setIsMfaResending(false);
    }
  };
  const ShowPasswordButton = (
    <button
      className={'main:right-[8px] relative right-[20px]'}
      onClick={() => {
        if (passwordInputType === 'password') {
          setPasswordInputType('text');
        } else {
          setPasswordInputType('password');
        }
      }}
    >
      <img
        src={
          passwordInputType === 'password'
            ? '/icons/hide-password.svg'
            : '/icons/show-password.svg'
        }
        alt={'show password button'}
      />
    </button>
  );

  return (
    <>
      {mfaChallenge ? (
        <EmailMfaVerification
          code={mfaCode}
          setCode={setMfaCode}
          maskedEmail={mfaChallenge.maskedEmail}
          status={mfaStatus}
          message={mfaMsg}
          submit={submitMfa}
          resend={resendMfa}
          isSubmitting={isMfaSubmitting}
          isResending={isMfaResending}
          back={() => {
            setMfaChallenge(null);
            setMfaCode('');
          }}
        />
      ) : (
        <>
          <SignIn
            emailStatus={emailStatus}
            email={email}
            emailMsg={emailMsg}
            setEmail={setEmail}
            passwordStatus={passwordStatus}
            password={password}
            passwordMsg={passwordMsg}
            passwordInputType={passwordInputType}
            ShowPasswordButton={ShowPasswordButton}
            isPersistent={isPersistent}
            setIsPersistent={setIsPersistent}
            submit={submit}
            setPassword={setPassword}
          />
          <div className={'flex justify-center gap-[40px]'}>
            <a href={'/auth/password/request'} className={'text-gray-400'}>
              {t('비밀번호 찾기')}
            </a>
            <a href={'/auth/sign-up'} className={'text-primary'}>
              {t('회원가입')}
            </a>
          </div>
        </>
      )}
    </>
  );
};

export default LandingSignIn;
